import React, { useState } from 'react'

import { Modal, Form, Input, Button, Typography } from 'antd'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

interface EnterpriseContactModalProps {
  open: boolean
  onCancel: () => void
  onSubmit: (values: { name: string; email: string; message?: string }) => Promise<void> | void
}

const { Text } = Typography

export const EnterpriseContactModal: React.FC<EnterpriseContactModalProps> = ({
  open,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation()

  const [form] = Form.useForm<{ name: string; email: string; message?: string }>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFinish = async (values: { name: string; email: string; message?: string }) => {
    try {
      setIsSubmitting(true)
      await onSubmit(values)
      form.resetFields()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <SModal
      title={(
        <SModalTitle>
          {t('l_plans_contactFormTitle')}
        </SModalTitle>
      )}
      open={open}
      onCancel={onCancel}
      footer={(
        <SFooter>
          <Button onClick={onCancel}
            size="large"
          >
            {t('a_common_cancel')}
          </Button>

          <Button type="primary"
            size="large"
            onClick={() => form.submit()}
            loading={isSubmitting}
          >
            {t('a_common_send')}
          </Button>
        </SFooter>
      )}
      centered
      width={520}
      destroyOnClose
    >
      <SDescription type="secondary">
        {t('l_plans_contactFormMsg')}
      </SDescription>

      <SForm
        form={form as any}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
      >
        <Form.Item
          label={(
            <SLabel>
              {t('l_common_name')}
            </SLabel>
          )}
          name="name"
          rules={[{ required: true, message: 'Please enter your name' }]}
        >
          <Input size="large"
            placeholder={t('l_placeholder_name')}
          />
        </Form.Item>

        <Form.Item
          label={(
            <SLabel>
              {t('l_common_email')}
            </SLabel>
          )}
          name="email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input size="large"
            placeholder={t('l_placeholder_email')}
          />
        </Form.Item>

        <Form.Item
          label={(
            <SLabel>
              {t('l_common_message')}
            </SLabel>
          )}
          name="message"
        >
          <Input.TextArea
            placeholder={t('l_placeholder_message')}
            autoSize={{ minRows: 5, maxRows: 8 }}
          />
        </Form.Item>
      </SForm>
    </SModal>
  )
}

const SModal = styled(Modal)`
  max-width: 520px;

  .ant-modal-content {
    border-radius: 12px;
  }

  .ant-modal-body {
    padding-top: 8px;
  }
`

const SModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: #000000E0;
`

const SDescription = styled(Text)`
  display: block;
  margin-top: 4px;
  margin-bottom: 20px;
`

const SForm = styled(Form)`
  .ant-input-lg, .ant-input-textarea {
    border-radius: 8px;
  }

  label span {
    font-weight: 400;
  }
`

const SLabel = styled.span`
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
  color: #000000E0;
`

const SFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`


